import React, { useEffect, useState } from "react";
import { IoChevronForward } from "react-icons/io5";
import AppModal from "../../../components/layout/app/AppModal";
import { useToggleTop } from "../../../components/common/ToggleTop";
import { sendControl } from "../../../services/deviceControl";
import { UserService } from "../../../services/UserService";
import { NumberField, inRange, settingsPayload, toText, useAlive } from "./shared";
import "../../../assets/styles/app-meter-settings.css";

// Pieces shared by the energy meter's Settings and Alarm Setting pages
// (app/screens/DeviceSettings/EnergyMeterSettings.js, app/screens/
// EnergyMeterAlarmSetting.js). Each setting is one command, { id, trait,
// value }, and the meter reports what it holds in its device settings.

// The two screens' toasts.
const SAVED = "Updated successfully!";
const ERRORS = { failed: "An error occurred while updating.", empty: "Update failed. Please try again." };

const EDITOR_FORM = "hx-set-em-editor";
const EDITOR_INPUT = "hx-set-em-value";

// Reads the meter's saved settings (an empty set when it hasn't reported any,
// so the page shows the app's defaults) and sends changes one at a time
// behind the loader. A change shows once the meter has taken it.
export const useMeterSettings = (device) => {
  const toast = useToggleTop();
  const alive = useAlive();
  const deviceId = device?.deviceId;
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!deviceId) return undefined;
    let current = true;
    setStatus("loading");
    UserService.getDevice(deviceId)
      .then((response) => {
        if (!current) return;
        const saved = response?.[0]?.device?.deviceSettings?.[0]?.settings;
        setSettings(saved && typeof saved === "object" ? saved : {});
        setStatus("ready");
      })
      .catch(() => {
        if (current) setStatus("error");
      });
    return () => {
      current = false;
    };
  }, [deviceId, attempt]);

  // key is the name the meter reports the setting under.
  const send = async (command, key) => {
    setBusy("Updating settings…");
    let outcome;
    try {
      outcome = (await sendControl(settingsPayload(device, command))) ? "saved" : "empty";
    } catch (error) {
      outcome = "failed";
    }
    if (alive.current) {
      if (outcome === "saved") setSettings((current) => ({ ...current, [key]: command.value }));
      setBusy(null);
    }
    if (outcome === "saved") toast(SAVED, "green");
    else toast(ERRORS[outcome], "red");
  };

  return { settings, status, busy, send, retry: () => setAttempt((count) => count + 1) };
};

// The value a setting shows: what the meter reports, or the app's default.
export const valueOf = (settings, key, fallback = "0") => {
  const value = settings?.[key];
  return value === undefined || value === null || value === "" ? fallback : String(value);
};

export const isEnabled = (settings, key) => Boolean(Number(settings?.[key]));

// A row that opens an editor, a list or (opensPage) another page: title, the
// app's grey description, then the value and a chevron.
export const ValueRow = ({ title, description, value, opensPage = false, onOpen }) => (
  <li>
    <button type="button" className="hx-set-em-row" aria-haspopup={opensPage ? undefined : "dialog"} onClick={onOpen}>
      <span className="hx-set-em-text">
        <span className="hx-set-em-title">{title}</span>
        {description ? <span className="hx-set-em-description">{description}</span> : null}
      </span>
      {value !== undefined ? <span className="hx-set-em-value">{value}</span> : null}
      <IoChevronForward aria-hidden="true" />
    </button>
  </li>
);

// An on/off row. The title labels the switch and the text under it describes it.
export const SwitchRow = ({ id, title, description, checked, onToggle }) => (
  <li className="hx-set-em-row">
    <span className="hx-set-em-text">
      <label className="hx-set-em-title" htmlFor={id}>
        {title}
      </label>
      {description ? (
        <span id={`${id}-description`} className="hx-set-em-description">
          {description}
        </span>
      ) : null}
    </span>
    <span className="hx-set-em-switch">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        aria-describedby={description ? `${id}-description` : undefined}
        onChange={onToggle}
      />
      <span className={`hx-switch ${checked ? "is-on" : ""}`} aria-hidden="true">
        <span className="hx-switch-knob" />
      </span>
    </span>
  </li>
);

// The app's number editor: the setting's name, its range and a number box.
// editor is { title, value, min, max, unit }; onSave gets the number.
export const NumberDialog = ({ editor, onClose, onSave }) => {
  const [text, setText] = useState(toText(editor.value));
  const [invalid, setInvalid] = useState(false);

  // Rendered by the page, so this runs after the dialog has noted where to
  // send focus back to.
  useEffect(() => {
    document.getElementById(EDITOR_INPUT)?.focus();
  }, []);

  const submit = (event) => {
    event.preventDefault();
    if (!inRange(text, editor.min, editor.max)) {
      setInvalid(true);
      document.getElementById(EDITOR_INPUT)?.focus();
      return;
    }
    onSave(Number(text));
  };

  const unit = editor.unit ? ` ${editor.unit}` : "";
  return (
    <AppModal
      open
      onClose={onClose}
      title={editor.title}
      actions={
        <>
          <button type="button" className="hx-btn is-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={EDITOR_FORM} className="hx-btn">
            Save
          </button>
        </>
      }
    >
      <form id={EDITOR_FORM} noValidate onSubmit={submit}>
        <NumberField
          id={EDITOR_INPUT}
          label={editor.unit ? `Value (${editor.unit})` : "Value"}
          value={text}
          placeholder="Please enter the value"
          hint={`Range: ${editor.min} ~ ${editor.max}${unit}`}
          invalid={invalid}
          onChange={(next) => {
            setText(next);
            setInvalid(false);
          }}
        />
      </form>
    </AppModal>
  );
};
